/* eslint-disable react/jsx-wrap-multilines */
import styled from 'styled-components';
import { useState } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import useInterval from '@hooks/useInterval';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import TextField from '@base/TextField/TextField';
import Button from '@base/Button/Button';
import { getOtpError } from '@utils/formHelper';
import { Spacings, Fonts, BgColors, TextColors } from '@styles/index';
import EnterOtherFields from './EnterOtherFields';

const StyledP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_4B};
`;

const StyledTextField = styled(TextField)`
  margin-top: ${Spacings.SPACING_4B};
`;

const DisabledTextField = styled(TextField)`
  /* padding-left: 108px; */
  background: ${BgColors.DISABLED};
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
  min-width: ${({ fixedWidth }) => (fixedWidth ? '140px' : '28px')};
`;

const StyledH6WithMargins = styled(H6)`
  font-weight: ${Fonts.BOLD};
  margin: 0 ${Spacings.SPACING_2B};
  padding-bottom: ${Spacings.SPACING_2B};
`;

const StyledUnderlineLink = styled(UnderlineLink)`
  border-bottom-width: 0;
  padding-bottom: 0;
`;

const UnderlineLinkWrapper = styled.div`
  position: absolute;
  right: ${Spacings.SPACING_4B};
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      right: 0px;
    };`}
`;

const EditDiv = styled.div`
  position: relative;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  margin-top: ${Spacings.SPACING_8B};
`;

const Header = styled(H2)`
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      font-size: 16px;
  };`}
`;

const SubmitButton = styled(Button)`
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      width: 110px;
  };`}
`;

const EnterEmailOtpStep = ({
  email,
  country,
  otp,
  setOtp,
  resendOtp,
  editEmail,
  fieldsData,
  setFieldsData,
  otpOrEmailResponse,
  submitLogin,
  loggingIn,
  states,
  error
}) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const startTimer = () => {
    setTimeLeft(60);
  };
  const onOtpChange = (e) => {
    const otpFilled = e.target.value.replace(/ /g, '');
    if (otpFilled.match(/^\d+$/) || otpFilled === '') {
      setOtp(otpFilled);
    }
  };
  const resend = (e) => {
    if (e) e.preventDefault();
    if (timeLeft === 0) {
      startTimer();
      resendOtp(1);
    }
  };
  const onCall = (e) => {
    if (e) e.preventDefault();
    if (timeLeft === 0) {
      startTimer();
      resendOtp(2);
    }
  };

  const shouldEnableSubmit = () => {
    let enabled = true;
    if (otp === '') {
      enabled = false;
    }
    if (!otpOrEmailResponse.has_name && fieldsData.name.value.trim() === '') {
      enabled = false;
    }
    if (!otpOrEmailResponse.has_email && fieldsData.email.value.trim() === '') {
      enabled = false;
    }
    if (
      country?.code === 'IN' &&
      !otpOrEmailResponse.has_state &&
      fieldsData.state.value.trim() === ''
    ) {
      enabled = false;
    }
    return enabled;
  };

  const onButtonClick = (e) => {
    e.preventDefault();
    if (shouldEnableSubmit()) submitLogin();
  };
  const btnLabel = 'Submit';
  const subHeading = "We've sent an OTP to your registered email address";

  const getTimeLeftStr = () => {
    if (timeLeft > 0) {
      const str = `0${timeLeft}`;
      return ` in 00:${String(str).slice(-2)}`;
    }
    return '';
  };

  useInterval(
    () => {
      setTimeLeft(timeLeft - 1);
    },
    timeLeft > 0 ? 1000 : null
  );

  const { isOtpError, otpErrorMsg } = getOtpError(error);
  return (
    <>
      <Header>Enter OTP</Header>
      <StyledP1>{subHeading}</StyledP1>
      <EditDiv>
        <DisabledTextField
          fullWidth
          value={email}
          type="email"
          disabled
          endAdornment={
            <InputAdornment>
              <StyledUnderlineLink onClick={editEmail} href="#">
                <StyledH6>Edit</StyledH6>
              </StyledUnderlineLink>
            </InputAdornment>
          }
        />
      </EditDiv>
      <form onSubmit={onButtonClick}>
        <StyledTextField
          fullWidth
          value={otp}
          type="tel"
          placeholder="One time password"
          onChange={onOtpChange}
          autoFocus
          inputProps={{
            type: 'tel',
            maxLength: 6,
            minLength: 4,
            required: true
          }}
          error={isOtpError}
          helperText={otpErrorMsg}
        />
        <EnterOtherFields
          fieldsData={fieldsData}
          setFieldsData={setFieldsData}
          otpOrEmailResponse={otpOrEmailResponse}
          states={states}
          country={country}
          error={error}
        />
      </form>
      <ButtonWrapper>
        <SubmitButton
          variant={shouldEnableSubmit() && !loggingIn ? 'filled' : 'disabled'}
          label={loggingIn ? 'Submitting...' : btnLabel}
          onClick={onButtonClick}
        />
        <UnderlineLinkWrapper>
          <UnderlineLink disabled={timeLeft > 0} onClick={resend} href="#">
            <StyledH6
              color={timeLeft > 0 ? TextColors.DISABLED : TextColors.BLACK}
              fixedWidth={timeLeft > 0}
            >
              {`Resend OTP ${getTimeLeftStr()}`}
            </StyledH6>
          </UnderlineLink>
          {timeLeft === 0 ? (
            <StyledH6WithMargins color={TextColors.DISABLED}>
              or
            </StyledH6WithMargins>
          ) : null}
          {timeLeft === 0 ? (
            <UnderlineLink onClick={onCall} href="#">
              <StyledH6>Call</StyledH6>
            </UnderlineLink>
          ) : null}
        </UnderlineLinkWrapper>
      </ButtonWrapper>
    </>
  );
};

export default EnterEmailOtpStep;

EnterEmailOtpStep.propTypes = {
  email: PropTypes.string.isRequired,
  country: PropTypes.objectOf(PropTypes.any).isRequired,
  states: PropTypes.arrayOf(PropTypes.any).isRequired,
  otp: PropTypes.string.isRequired,
  fieldsData: PropTypes.objectOf(PropTypes.any).isRequired,
  setOtp: PropTypes.func.isRequired,
  resendOtp: PropTypes.func.isRequired,
  editEmail: PropTypes.func.isRequired,
  setFieldsData: PropTypes.func.isRequired,
  submitLogin: PropTypes.func.isRequired,
  loggingIn: PropTypes.bool.isRequired,
  otpOrEmailResponse: PropTypes.objectOf(PropTypes.any).isRequired,
  error: PropTypes.objectOf(PropTypes.any).isRequired
};
